<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { onMount, onDestroy } from "svelte";
  import type { Trip, Schedule } from "../../lib/types";
  import { schedulesApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import { categoryIcons } from "../../lib/icons";
  import Button from "../../components/input/Button.svelte";

  let { trip, schedules = [], user = null, googleMapsApiKey = "" }: {
    trip: Trip;
    schedules?: Schedule[];
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    googleMapsApiKey?: string;
  } = $props();

  let mapContainer: HTMLDivElement;
  let map: google.maps.Map | null = null;
  let markers: google.maps.Marker[] = [];
  let directionsService: google.maps.DirectionsService | null = null;
  let directionsRenderer: google.maps.DirectionsRenderer | null = null;
  let placesService: google.maps.places.PlacesService | null = null;
  let autocomplete: google.maps.places.Autocomplete | null = null;

  let selectedDate = $state("");
  let searchQuery = $state("");
  let searchResults: google.maps.places.PlaceResult[] = $state([]);
  let searching = $state(false);
  let showAddModal = $state(false);
  let selectedPlace: google.maps.places.PlaceResult | null = $state(null);
  let draggingSchedule: Schedule | null = $state(null);
  let selectedDateHydrated = false;

  // 날짜 목록
  let tripDates = $derived(generateDateRange(trip.startDate, trip.endDate));

  // 선택된 날짜의 일정들
  let daySchedules = $derived(schedules
    .filter(s => s.date === selectedDate)
    .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00")));

  // 예상 비용 계산
  let estimatedBudget = $derived(calculateEstimatedBudget());

  $effect(() => {
    if (selectedDateHydrated) return;
    selectedDate = trip.startDate;
    selectedDateHydrated = true;
  });

  const categories = [
    { key: "activity", label: "액티비티", icon: categoryIcons.activity, avgCost: 50000 },
    { key: "food", label: "식사", icon: categoryIcons.food, avgCost: 30000 },
    { key: "transport", label: "이동", icon: categoryIcons.transport, avgCost: 15000 },
    { key: "accommodation", label: "숙소", icon: categoryIcons.accommodation, avgCost: 150000 },
    { key: "shopping", label: "쇼핑", icon: categoryIcons.shopping, avgCost: 100000 },
    { key: "other", label: "기타", icon: categoryIcons.other, avgCost: 20000 },
  ];

  function generateDateRange(start: string, end: string): string[] {
    const dates: string[] = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
    return `${date.getMonth() + 1}/${date.getDate()} (${weekdays[date.getDay()]})`;
  }

  function getDayNumber(dateStr: string): number {
    return tripDates.indexOf(dateStr) + 1;
  }

  function calculateEstimatedBudget() {
    let total = 0;
    const byCategory: Record<string, number> = {};
    
    schedules.forEach(s => {
      const cat = categories.find(c => c.key === s.category) || categories[5];
      total += cat.avgCost;
      byCategory[cat.key] = (byCategory[cat.key] || 0) + cat.avgCost;
    });

    return { total, byCategory, perPerson: Math.round(total / (trip.participants?.length || 1)) };
  }

  onMount(() => {
    loadGoogleMaps();
  });

  onDestroy(() => {
    markers.forEach(m => m.setMap(null));
  });

  function loadGoogleMaps() {
    if (typeof google !== "undefined" && google.maps) {
      initMap();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    
    (window as any).initGoogleMaps = initMap;
    document.head.appendChild(script);
  }

  function initMap() {
    if (!mapContainer) return;

    // 기본 위치 (서울 또는 첫 일정의 위치)
    const defaultCenter = { lat: 37.5665, lng: 126.9780 };

    map = new google.maps.Map(mapContainer, {
      center: defaultCenter,
      zoom: 12,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    directionsService = new google.maps.DirectionsService();
    directionsRenderer = new google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#4F46E5",
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    placesService = new google.maps.places.PlacesService(map);

    // Autocomplete 설정
    const input = document.getElementById("place-search") as HTMLInputElement;
    if (input) {
      autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["establishment", "geocode"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete?.getPlace();
        if (place && place.geometry) {
          selectedPlace = place;
          showAddModal = true;
          map?.panTo(place.geometry.location!);
          map?.setZoom(15);
        }
      });
    }

    updateMarkers();
    drawRoute();
  }

  function updateMarkers() {
    if (!map) return;

    // 기존 마커 제거
    markers.forEach(m => m.setMap(null));
    markers = [];

    // 새 마커 추가
    daySchedules.forEach((schedule, index) => {
      if (!schedule.location) return;

      // Geocoding으로 위치 찾기
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: schedule.location }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const marker = new google.maps.Marker({
            map,
            position: results[0].geometry.location,
            label: {
              text: String(index + 1),
              color: "white",
              fontWeight: "bold",
            },
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 20,
              fillColor: "#4F46E5",
              fillOpacity: 1,
              strokeColor: "#312E81",
              strokeWeight: 2,
            },
            title: schedule.title,
          });

          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <strong>${schedule.title}</strong>
                <p style="margin: 4px 0; color: #666;">${schedule.location}</p>
                <p style="margin: 0; font-size: 12px; color: #999;">${schedule.startTime || ""} ${schedule.endTime ? "- " + schedule.endTime : ""}</p>
              </div>
            `,
          });

          marker.addListener("click", () => {
            infoWindow.open(map, marker);
          });

          markers.push(marker);

          // 첫 마커로 지도 중심 이동
          if (index === 0) {
            map?.panTo(results[0].geometry.location);
          }
        }
      });
    });
  }

  function drawRoute() {
    if (!directionsService || !directionsRenderer || daySchedules.length < 2) {
      directionsRenderer?.setDirections({ routes: [] } as any);
      return;
    }

    const waypoints = daySchedules.slice(1, -1).map(s => ({
      location: s.location || "",
      stopover: true,
    }));

    directionsService.route(
      {
        origin: daySchedules[0].location || "",
        destination: daySchedules[daySchedules.length - 1].location || "",
        waypoints,
        travelMode: google.maps.TravelMode.TRANSIT,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK" && result) {
          directionsRenderer?.setDirections(result);
        }
      }
    );
  }

  $effect(() => {
    if (map && daySchedules) {
      updateMarkers();
      drawRoute();
    }
  });

  async function addScheduleFromPlace() {
    if (!selectedPlace) return;

    try {
      await schedulesApi.create(trip.id, {
        title: selectedPlace.name || "새 일정",
        location: selectedPlace.formatted_address || selectedPlace.vicinity || "",
        date: selectedDate,
        startTime: "10:00",
        endTime: "11:00",
        category: "activity",
      });

      addNotification("success", "일정이 추가되었습니다!");
      showAddModal = false;
      selectedPlace = null;
      router.reload();
    } catch (error) {
      addNotification("error", "일정 추가에 실패했습니다.");
    }
  }

  async function deleteSchedule(id: string) {
    if (!confirm("이 일정을 삭제하시겠습니까?")) return;
    
    try {
      await schedulesApi.delete(id);
      addNotification("success", "일정이 삭제되었습니다.");
      router.reload();
    } catch (error) {
      addNotification("error", "삭제에 실패했습니다.");
    }
  }

  // Note: MapPlanner uses raw amounts, not cents
  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("ko-KR").format(amount);
  }

  // 지도 스타일
  const mapStyles = [
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
  ];
</script>

<svelte:head>
  <script src="https://maps.googleapis.com/maps/api/js?key={googleMapsApiKey}&libraries=places" async defer></script>
</svelte:head>

<Layout {user}>
  <div class="h-screen flex flex-col bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="map" />

    <!-- Map Header -->
    <header class="z-20 flex-shrink-0" style="background: rgba(15,15,15,0.95); border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07);">
      <div class="max-w-full mx-auto px-4 py-3">
        <div class="flex items-center justify-between gap-4">
          <!-- Budget Preview -->
          <div class="flex items-center gap-6 px-4 py-2 rounded-xl" style="background: rgba(32,178,170,0.08); border: 1px solid rgba(32,178,170,0.15);">
            <div class="text-center">
              <p class="text-xs" style="color: var(--text-muted);">예상 총 비용</p>
              <p class="text-lg font-bold" style="color: var(--accent);">₩{formatCurrency(estimatedBudget.total)}</p>
            </div>
            <div class="w-px h-8" style="background: rgba(255,255,255,0.1);"></div>
            <div class="text-center">
              <p class="text-xs" style="color: var(--text-muted);">1인당</p>
              <p class="text-lg font-bold" style="color: var(--accent);">₩{formatCurrency(estimatedBudget.perPerson)}</p>
            </div>
          </div>

          <!-- Date Tabs -->
          <div class="flex gap-2 overflow-x-auto">
            {#each tripDates as date}
              <button
                onclick={() => selectedDate = date}
                class="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={selectedDate === date
                  ? 'background: rgba(32,178,170,0.2); color: #20b2aa; box-shadow: 0 0 10px rgba(32,178,170,0.2);'
                  : 'background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.6); border: 1px solid rgba(255,255,255,0.1);'}
              >
                Day {getDayNumber(date)}
                <span class="ml-1 text-xs opacity-75">{formatDate(date)}</span>
              </button>
            {/each}
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left Panel: Schedule List -->
      <div class="w-96 flex flex-col overflow-hidden" style="background: var(--surface); border-right: 1px solid rgba(255,255,255,0.07);">
        <!-- Search -->
        <div class="p-4" style="border-bottom: 1px solid rgba(255,255,255,0.07);">
          <div class="relative">
            <input
              id="place-search"
              type="text"
              placeholder="장소 검색 (예: 에펠탑, 명동 맛집...)"
              class="w-full pl-10 pr-4 py-3 rounded-xl transition-all"
              style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: var(--text-primary);"
            />
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <!-- Schedule List -->
        <div class="flex-1 overflow-y-auto p-4">
          {#if daySchedules.length === 0}
            <div class="text-center py-12" style="color: var(--text-muted);">
              <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted); opacity: 0.4;">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p class="font-medium">일정이 없습니다</p>
              <p class="text-sm mt-1">위에서 장소를 검색해 추가하세요!</p>
            </div>
          {:else}
            <div class="space-y-3">
              {#each daySchedules as schedule, index}
                {@const cat = categories.find(c => c.key === schedule.category) || categories[5]}
                <div
                  class="rounded-xl p-4 transition-all cursor-pointer relative group"
                  style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);"
                  role="listitem"
                  onmouseenter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
                  onmouseleave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                  draggable="true"
                  ondragstart={() => draggingSchedule = schedule}
                  ondragend={() => draggingSchedule = null}
                >
                  <div class="flex items-start gap-3">
                    <div class="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">{@html cat.icon}</svg>
                        <h3 class="font-semibold truncate" style="color: var(--text-primary);">{schedule.title}</h3>
                      </div>
                      {#if schedule.location}
                        <p class="text-sm truncate mt-1" style="color: var(--text-muted);">{schedule.location}</p>
                      {/if}
                      <div class="flex items-center gap-3 mt-2 text-xs" style="color: var(--text-muted);">
                        {#if schedule.startTime}
                          <span>{schedule.startTime} {schedule.endTime ? `- ${schedule.endTime}` : ""}</span>
                        {/if}
                        <span style="color: var(--accent);">예상 ₩{formatCurrency(cat.avgCost)}</span>
                      </div>
                    </div>
                    <button
                      aria-label="일정 삭제"
                      onclick={(e) => { e.stopPropagation(); deleteSchedule(schedule.id); }}
                      class="opacity-0 group-hover:opacity-100 p-1 transition-all"
                      style="color: var(--text-muted);"
                      onmouseenter={(e) => (e.currentTarget as HTMLElement).style.color='#f87171'}
                      onmouseleave={(e) => (e.currentTarget as HTMLElement).style.color='var(--text-muted)'}
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <!-- Route Info -->
                  {#if index < daySchedules.length - 1}
                    <div class="mt-3 pt-3 flex items-center gap-2 text-xs" style="border-top: 1px solid rgba(255,255,255,0.07); color: var(--text-muted);">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      <span>다음 장소로 이동</span>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Day Summary -->
        <div class="p-4" style="border-top: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.03);">
          <div class="flex items-center justify-between text-sm">
            <span style="color: var(--text-secondary);">Day {getDayNumber(selectedDate)} 예상 비용</span>
            <span class="font-bold" style="color: var(--accent);">
              ₩{formatCurrency(daySchedules.reduce((sum, s) => {
                const cat = categories.find(c => c.key === s.category) || categories[5];
                return sum + cat.avgCost;
              }, 0))}
            </span>
          </div>
        </div>
      </div>

      <!-- Right Panel: Map -->
      <div class="flex-1 relative">
        <div bind:this={mapContainer} class="w-full h-full"></div>
        
        <!-- Map Legend -->
        <div class="absolute bottom-4 left-4 rounded-xl p-4" style="background: rgba(15,15,15,0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 8px 24px rgba(0,0,0,0.5);">
          <p class="text-xs font-medium mb-2" style="color: var(--text-muted);">카테고리별 평균 비용</p>
          <div class="grid grid-cols-2 gap-2 text-xs">
            {#each categories as cat}
              <div class="flex items-center gap-2">
                <span>{cat.icon}</span>
                <span style="color: var(--text-secondary);">{cat.label}</span>
                <span style="color: var(--accent);">₩{formatCurrency(cat.avgCost)}</span>
              </div>
            {/each}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Schedule Modal -->
  <Modal show={showAddModal} title="일정 추가" onClose={() => { showAddModal = false; selectedPlace = null; }}>
    {#if selectedPlace}
      <div class="space-y-4">
        <div class="rounded-xl p-4" style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);">
          <h3 class="font-semibold" style="color: var(--text-primary);">{selectedPlace.name}</h3>
          <p class="text-sm mt-1" style="color: var(--text-muted);">{selectedPlace.formatted_address || selectedPlace.vicinity}</p>
          {#if selectedPlace.rating}
            <div class="flex items-center gap-1 mt-2">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" style="color: #fbbf24;">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span class="text-sm" style="color: var(--text-secondary);">{selectedPlace.rating}</span>
            </div>
          {/if}
        </div>

        <p class="text-sm" style="color: var(--text-secondary);">
          <strong style="color: var(--text-primary);">Day {getDayNumber(selectedDate)}</strong> ({formatDate(selectedDate)})에 추가됩니다.
        </p>

        <div class="flex gap-3">
          <Button variant="secondary" class="flex-1" onclick={() => { showAddModal = false; selectedPlace = null; }}>취소</Button>
          <Button variant="primary" class="flex-1" onclick={addScheduleFromPlace}>일정 추가</Button>
        </div>
      </div>
    {/if}
  </Modal>
</Layout>
